import React, { useCallback, useState } from "react";
import { Menu, Drawer, Alert, RowAdjustments, Fab } from "./index";
import {
  CheckOutlined,
  InfoOutlined,
  AppsOutlined,
  CreateOutlined,
  DeleteForeverOutlined,
  Attachment,
  AddPhotoAlternate,
} from "@mui/icons-material";
import {
  getProductInfoByRowNumber,
  getProductInfoByBarcode,
  getProductInfoCustom,
} from "../modules/api/productInfo";
import {
  manageIsFullyCollected,
  deleteRecord,
  manageFiles,
  getDocDetails,
} from "../modules/api/docListsandDetails";
import { useAppSelector, useAppDispatch } from "../app/hook";
import { setUserRefreshData } from "../modules/auth/slices/userSlice";
import {
  userClientDb,
  uiLanguage,
  refreshData,
  useExtraField1,
  useExtraField2,
  useExtraField3,
  useExtraField4,
  useExtraField5,
  userDepartmentId,
  userDeleteRow,
} from "../modules/auth/selectors/userSelector";
import { getErrorMessage } from "./form/formUtils";
import { useAuthorizationError, useLocalStorage } from "../hooks";
import { useTranslation } from "react-i18next";
import * as modules from "../utils/modules";
import { PopoverVirtualElement } from "@mui/material";
import AttachmentsViewer from "./AttachmentsViewer";
import PhotosCamera from "./PhotosCamera";
import useRowMenuFileItemStyles from "./css/useRowMenuFileItemStyles";
import useFabStyles from "./css/useFabStyles";

interface IRowMenu {
  open: boolean;
  onClose: () => void;
  anchorEl:
    | Element
    | (() => Element)
    | PopoverVirtualElement
    | (() => PopoverVirtualElement)
    | null
    | undefined;
  complete?: boolean;
  detailedInfo: {
    element1?: string;
    element2?: string;
    isFullyCollected?: number;
    quantityApp?: number;
    quantityDocument?: number;
    fraction?: number;
    rowNumber?: number;
    module?: string;
    operationCode?: string;
    operationType?: string;
    lot?: string;
    objectCode?: string;
    productCode?: string;
    unitCode?: string;
    barcode?: string;
    extraInfo1?: string;
    extraInfo2?: string;
    extraInfo3?: string;
    extraInfo4?: string;
    extraInfo5?: string;
    id?: string | number;
    fileCount?: number;
  };
  updateRowInList?: (updatedRow: any) => void;
}

interface RowDetail {
  Row_number?: string;
  barcode?: string;
  departmentCode?: string;
  element1?: string;
  element2?: string;
  expDate?: string;
  extraInfo1?: string;
  extraInfo2?: string;
  extraInfo3?: string;
  extraInfo4?: string;
  extraInfo5?: string;
  fileCount?: string | number;
  fileDelete?: string | number;
  fileInsert?: string | number;
  fraction?: string | number;
  id?: string | number;
  isFullyCollected?: string | number;
  module?: string;
  mscanCode?: string;
  note?: string;
  numberOfElements?: string | number;
  operationCode?: string;
  operationType?: string;
  pageNumber?: string | number;
  productCode?: string;
  productName?: string;
  quantityApp?: string | number;
  quantityDocument?: string | number;
  rowNumber?: string | number;
  size?: string | number;
  totalElements?: string | number;
  unitCode?: string;
  updateDate?: string;
}
function RowMenu(props: IRowMenu) {
  const { detailedInfo, open, onClose, anchorEl, complete, updateRowInList } =
    props;
  const [openDrawer, setOpenDrawer] = useState(false);
  const locale = useAppSelector(uiLanguage);
  const userDb = useAppSelector(userClientDb);
  const refresh = useAppSelector(refreshData);
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const [extraInfo, setExtraInfo] = useState({});
  const [logoutUser] = useAuthorizationError();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [openAdjDialog, setOpenAdjDialog] = useState(false);
  const [isCustomProc] = useLocalStorage("productCustomProc");
  const extra1 = useAppSelector(useExtraField1);
  const extra2 = useAppSelector(useExtraField2);
  const extra3 = useAppSelector(useExtraField3);
  const extra4 = useAppSelector(useExtraField4);
  const extra5 = useAppSelector(useExtraField5);
  const departmentId = useAppSelector(userDepartmentId);
  const canUserDeleteRow = useAppSelector(userDeleteRow);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const [openCamera, setOpenCamera] = useState(false);
  const [openAttachments, setOpenAttachments] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [rowDetail, setRowDetail] = useState<RowDetail>(detailedInfo);

  const { classes } = useRowMenuFileItemStyles();
  const { classes: fabClasses } = useFabStyles();

  const handleDetailedInfo = async () => {
    try {
      const params = {
        clientDbId: userDb ? userDb.id : 0,
        module: detailedInfo.module || "",
        operationCode: detailedInfo.operationCode || "",
        departmentId: departmentId,
        locale: locale,
        lineType: 2,
        rowNumber: detailedInfo.rowNumber,
        appId: detailedInfo.id,
      };

      const res = await getDocDetails(params);

      const isSchema =
        Array.isArray(res.data) &&
        res.data.length > 0 &&
        res.data[0].key !== undefined &&
        res.data[0].value !== undefined;

      let newData: any;

      if (isSchema) {
        newData = { schema: res.data };
      } else {
        newData = res.data.map((item: any) => {
          let cleanedData: { [key: string]: any } = { ...item };

          delete cleanedData.element1;
          delete cleanedData.element2;
          delete cleanedData.locationPriority;
          delete cleanedData.locations;
          delete cleanedData.numberOfElements;
          delete cleanedData.size;
          delete cleanedData.pageNumber;
          delete cleanedData.totalElements;
          delete cleanedData.params;
          delete cleanedData.Row_number;
          delete cleanedData.fileInsert;
          delete cleanedData.fileDelete;
          delete cleanedData.fileCount;

          const ex1 =
            extra1.type === "$B"
              ? JSON.stringify(cleanedData.extraInfo1)
              : cleanedData.extraInfo1;
          const ex2 =
            extra2.type === "$B"
              ? JSON.stringify(cleanedData.extraInfo2)
              : cleanedData.extraInfo2;
          const ex3 =
            extra3.type === "$B"
              ? JSON.stringify(cleanedData.extraInfo3)
              : cleanedData.extraInfo3;
          const ex4 =
            extra4.type === "$B"
              ? JSON.stringify(cleanedData.extraInfo4)
              : cleanedData.extraInfo4;
          const ex5 =
            extra5.type === "$B"
              ? JSON.stringify(cleanedData.extraInfo5)
              : cleanedData.extraInfo5;

          if (extra1.isUsed)
            cleanedData = {
              ...cleanedData,
              [extra1.name]: extra1.type === "$B" ? t(ex1) : ex1,
            };
          if (extra2.isUsed)
            cleanedData = {
              ...cleanedData,
              [extra2.name]: extra2.type === "$B" ? t(ex2) : ex2,
            };
          if (extra3.isUsed)
            cleanedData = {
              ...cleanedData,
              [extra3.name]: extra3.type === "$B" ? t(ex3) : ex3,
            };
          if (extra4.isUsed)
            cleanedData = {
              ...cleanedData,
              [extra4.name]: extra4.type === "$B" ? t(ex4) : ex4,
            };
          if (extra5.isUsed)
            cleanedData = {
              ...cleanedData,
              [extra5.name]: extra5.type === "$B" ? t(ex5) : ex5,
            };

          delete cleanedData.extraInfo1;
          delete cleanedData.extraInfo2;
          delete cleanedData.extraInfo3;
          delete cleanedData.extraInfo4;
          delete cleanedData.extraInfo5;

          return cleanedData;
        });
      }

      if (Array.isArray(newData)) {
        const mergedData = newData.reduce(
          (acc, item) => ({ ...acc, ...item }),
          {}
        );
        setExtraInfo(mergedData);
      } else {
        setExtraInfo(newData);
      }

      setOpenDrawer(true);
    } catch (error) {
      const errMessage = getErrorMessage(error);
      setError({
        error: true,
        message: errMessage.message,
        status: errMessage.status,
      });
    }
  };

  const handleInfoByRow = () => {
    getInfoByRow();
  };

  const handleCloseDrawer = () => {
    onClose();
    setOpenDrawer(false);
    setExtraInfo({});
  };

  const getInfoByRow = React.useCallback(() => {
    const params =
      detailedInfo.rowNumber && detailedInfo.rowNumber > 0
        ? {
            clientDbId: userDb ? userDb.id : 0,
            locale: locale,
            module: detailedInfo.module || "",
            rowNumber: detailedInfo.rowNumber || 0,
            operationCode: detailedInfo.operationCode || "",
            departmentId: departmentId,
            barcode: detailedInfo.barcode || "",
            version: "V3",
          }
        : {
            clientDbId: userDb ? userDb.id : 0,
            locale: locale,
            barcode: detailedInfo.barcode || "",
            module: detailedInfo.module,
            departmentId: departmentId,
            version: "V3",
          };

    const userProc = isCustomProc
      ? getProductInfoCustom
      : detailedInfo.rowNumber && detailedInfo.rowNumber > 0
      ? getProductInfoByRowNumber
      : getProductInfoByBarcode;

    userProc(params)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          if (data.length === 1 && !data[0].type && !data[0].key) {
            setExtraInfo(data[0]);
          } else {
            setExtraInfo({ schema: data });
          }
        } else if (data && typeof data === "object") {
          setExtraInfo(data);
        } else {
          setExtraInfo({});
        }
        setOpenDrawer(true);
      })
      .catch((reason: any) => {
        const errMessage = getErrorMessage(reason);
        setError({
          error: true,
          message: errMessage.message,
          status: errMessage.status,
        });
      });
  }, [detailedInfo, userDb, locale, departmentId, isCustomProc]);

  const handleClose = () => {
    logoutUser(error.status);

    if (error.status !== 401) {
      setError({ error: false, message: "", status: 0 });
      onClose();
    }
  };

  const markAsCollected = React.useCallback(
    () => {
      const params = {
        clientDbId: userDb ? userDb.id : 0,
        locale: locale,
        module: detailedInfo.module || "",
        rowNumber: detailedInfo.rowNumber || 0,
        operationCode: detailedInfo.operationCode || "",
        operationType: detailedInfo.operationType || "",
        lot: typeof detailedInfo.lot !== "undefined" ? detailedInfo.lot : "",
        objectCode:
          typeof detailedInfo.objectCode !== "undefined"
            ? detailedInfo.objectCode
            : "",
        productCode:
          typeof detailedInfo.productCode !== "undefined"
            ? detailedInfo.productCode
            : "",
        isFullyCollected:
          detailedInfo.isFullyCollected === 1 ||
          (typeof detailedInfo.isFullyCollected === "string" &&
            detailedInfo.isFullyCollected === "1")
            ? 0
            : 1,
        unitCode: detailedInfo.unitCode || "",
        barcode: detailedInfo.barcode || "",
      };

      manageIsFullyCollected(params)
        .then(() => {
          dispatch(setUserRefreshData(!refresh));
        })
        .catch((reason: any) => {
          const errMessage = getErrorMessage(reason);
          setError({
            error: true,
            message: errMessage.message,
            status: errMessage.status,
          });
        });
    }, // eslint-disable-next-line
    [manageIsFullyCollected, detailedInfo, userDb]
  );

  const handleOpenRowAdj = () => {
    setOpenAdjDialog(true);
  };

  const handleCloseRowAdj = () => {
    setOpenAdjDialog(false);
    onClose();
  };

  const refreshRowDetail = async () => {
    try {
      const res = await getDocDetails({
        clientDbId: userDb ? userDb.id : 0,
        module: rowDetail.module || "",
        operationCode: rowDetail.operationCode || "",
        rowNumber: rowDetail.rowNumber,
        locale,
      });

      if (Array.isArray(res?.data) && res.data.length > 0) {
        const row = res.data[0];

        setRowDetail(row);
        setExtraInfo(row);
        if (updateRowInList) {
          updateRowInList(row);
        }
      }
    } catch (err) {
      console.error("Nepavyko atnaujinti eilutės:", err);
    }
  };

  const loadAttachments = async () => {
    try {
      const params = {
        clientDbId: userDb ? userDb.id : 0,
        module: detailedInfo.module || "",
        operationCode: detailedInfo.operationCode || "",
        rowNumber: detailedInfo.rowNumber || 0,
        oper: "L",
        locale,
      };

      const res = await manageFiles(params);

      if ("data" in res) {
        setAttachments(res.data || []);
      } else {
        setAttachments([]);
      }
    } catch (err) {
      console.error("Nepavyko gauti priedų:", err);
    }
  };

  const handleAttachments = async () => {
    await loadAttachments();
    setOpenAttachments(true);
  };

  const showError = (
    <Alert
      isOpen={error.error}
      message={error.message}
      iconColor="error"
      onClose={handleClose}
    />
  );

  const handleCloseConfirmation = useCallback(() => {
    onClose();
    setOpenConfirmation(false);
  }, [onClose, setOpenConfirmation]);

  const handleDeleteConfirmation = useCallback(() => {
    const params = {
      clientDbId: userDb ? userDb.id : 0,
      locale,
      module: detailedInfo.module || "",
      rowNumber: detailedInfo.rowNumber || 0,
      operationCode: detailedInfo.operationCode || "",
      operationType: detailedInfo.operationType || "",
      id: detailedInfo.id || "",
    };

    deleteRecord(params)
      .then(() => {
        setOpenConfirmation(false);
        dispatch(setUserRefreshData(!refresh));
      })
      .catch((reason: any) => {
        setOpenConfirmation(false);
        const errMessage = getErrorMessage(reason);
        setError({
          error: true,
          message: errMessage.message,
          status: errMessage.status,
        });
      });
  }, [deleteRecord, detailedInfo, userDb]);

  const deleteRowConfirmation = (
    <Alert
      isOpen={openConfirmation}
      context="deleteRecord"
      iconColor="warning"
      onClose={handleCloseConfirmation}
      onConfirm={handleDeleteConfirmation}
      isConfirmationButton
      message={t("deleteRecord", "Ar tikrai norite išmesti įrašą?")}
    />
  );

  const handleRowDeletion = useCallback(() => {
    setOpenConfirmation(true);
  }, [setOpenConfirmation]);

  // menuItems be useMemo
  const menuItems = [
    {
      name: t("rowInformation", "Informacija apie eilutę"),
      icon: <InfoOutlined />,
      onClick: handleDetailedInfo,
    },
    {
      name: t("informationByBarcode", "Informacija pagal barkodą"),
      icon: <AppsOutlined />,
      onClick: handleInfoByRow,
    },
  ];

  if (!complete) {
    menuItems.push({
      name: t("markAsCollected", "Pažymėti kaip atrinktą / neatrinktą"),
      icon: <CheckOutlined />,
      onClick: markAsCollected,
    });

    menuItems.push({
      name: t("rowAdjustment", "Eilutės koregavimas"),
      icon: <CreateOutlined />,
      onClick: handleOpenRowAdj,
    });

    if (
      canUserDeleteRow &&
      detailedInfo &&
      !(
        detailedInfo.operationType === "PAJ" &&
        detailedInfo.module === modules.MODULE_INTERNAL
      )
    ) {
      menuItems.push({
        name: t("delete", "Ištrinti"),
        icon: <DeleteForeverOutlined />,
        onClick: handleRowDeletion,
      });
    }

    if (Number(rowDetail?.fileInsert) > 0) {
      menuItems.push({
        name: t("addPhoto", "Pridėti nuotrauką"),
        icon: <AddPhotoAlternate />,
        onClick: () => setOpenCamera(true),
      });
    }

    if (Number(detailedInfo?.fileCount) > 0) {
      menuItems.push({
        name: "",
        icon: (
          <div className={classes.wrapper}>
            {/* Kairė pusė: ikona + tekstas */}
            <div className={classes.leftSide}>
              <Attachment style={{ fontSize: 22 }} />
              <span className={classes.text}>
                {t("attachedFiles", "Prisegti failai")}
              </span>
            </div>
            {/* Dešinė pusė: žalias apskritimas */}
            <Fab
              size="small"
              variant="menu"
              className={classes.fab}
              icon={
                <span className={fabClasses.fabMenuCount}>
                  {detailedInfo.fileCount}
                </span>
              }
            />
          </div>
        ),
        onClick: handleAttachments,
      });
    }
  }
  return (
    <div>
      {error.error && showError}

      {deleteRowConfirmation}

      {openDrawer && (
        <Drawer
          isOpen={openDrawer}
          onClose={handleCloseDrawer}
          data={extraInfo}
        />
      )}

      {openAdjDialog && (
        <RowAdjustments
          open={openAdjDialog}
          onClose={handleCloseRowAdj}
          rowData={detailedInfo}
        />
      )}

      {openCamera && (
        <PhotosCamera
          open={openCamera}
          onClose={() => {
            setOpenCamera(false);
            onClose();
          }}
          clientDbId={userDb ? userDb.id : 0}
          module={detailedInfo.module || ""}
          operationCode={detailedInfo.operationCode || ""}
          locale={locale}
          onRefresh={refreshRowDetail}
          rowNumber={detailedInfo.rowNumber}
        />
      )}

      {openAttachments && (
        <AttachmentsViewer
          open={openAttachments}
          onClose={() => {
            setOpenAttachments(false);
            onClose();
          }}
          attachments={attachments}
          clientDbId={userDb ? userDb.id : 0}
          module={detailedInfo.module || ""}
          operationCode={detailedInfo.operationCode || ""}
          locale={locale}
          onRefresh={loadAttachments}
          rowNumber={detailedInfo.rowNumber}
          onRefreshRow={refreshRowDetail}
          fileDelete={rowDetail?.fileDelete}
        />
      )}

      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        data={menuItems}
      />
    </div>
  );
}

export default RowMenu;
